import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { ArrowUpRight } from "@phosphor-icons/react";
import { RevealText } from "@/components/shared/RevealText";
import { CTAButton } from "@/components/shared/CTAButton";

const HERO_IMAGE =
  "https://images.unsplash.com/photo-1659355893982-cb1e87a8758a?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export const HeroSection = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 130]);

  return (
    <section ref={ref} className="relative min-h-[92vh] flex items-end pt-32 pb-16 lg:pb-24 px-6 overflow-hidden bg-navy">
      <motion.div style={{ y }} className="absolute inset-0">
        <img
          src={HERO_IMAGE}
          alt="Professional teacher in a modern classroom"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/70 to-navy/30" />
      </motion.div>

      <div className="relative max-w-7xl mx-auto w-full">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1, duration: 0.6 }}
          data-testid="hero-credibility-statement"
          className="text-xs sm:text-sm uppercase tracking-[0.25em] text-accent font-semibold mb-6"
        >
          Supporting school recruitment since 2011
        </motion.p>

        <RevealText
          as="h1"
          text="Connecting Schools with Exceptional Teachers"
          delay={0.15}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-cream leading-[1.05] tracking-tight max-w-3xl"
        />

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.7 }}
          className="mt-6 text-cream/75 text-base sm:text-lg max-w-xl leading-relaxed"
        >
          Mithra Jobs helps schools find, assess and connect with teaching professionals who match their staffing
          requirements.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 0.7 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <CTAButton to="/contact-us#enquiry" variant="primary" testId="hero-cta-primary">
            Find the Right Teacher <ArrowUpRight size={16} weight="bold" />
          </CTAButton>
          <CTAButton to="/contact-us" variant="ghost" testId="hero-cta-secondary">
            Talk to Mithra Jobs
          </CTAButton>
        </motion.div>
      </div>
    </section>
  );
};
